import { AlertTriangle, CircleCheck, CircleSlash, Clock } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/cn'
import { formatMinorCurrency, formatNumber, pluralize } from '@/lib/format'
import type { RefundSummary } from '@/types/api'
import { Skeleton } from '@/components/ui/Skeleton'
import styles from './RefundSummaryCards.module.css'

interface RefundSummaryCardsProps {
  summary: RefundSummary | undefined
  isLoading: boolean
  isError: boolean
}

/**
 * The four numbers an operator wants before reading any rows.
 *
 * They are computed by the server over the same filters as the table below, so
 * narrowing to a month or a source moves both together — a summary that
 * disagreed with the rows under it would be worse than no summary.
 */
export function RefundSummaryCards({ summary, isLoading, isError }: RefundSummaryCardsProps) {
  if (isError) {
    return (
      <div className={styles.error} role="alert">
        <AlertTriangle aria-hidden="true" />
        <span>Refund totals could not be loaded. The ledger below is unaffected.</span>
      </div>
    )
  }

  if (isLoading || !summary) {
    return (
      <div className={styles.grid}>
        {[0, 1, 2, 3].map((index) => (
          <div key={index} className={styles.card}>
            <Skeleton width="60%" height={12} />
            <Skeleton width="80%" height={28} />
            <Skeleton width="45%" height={12} />
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className={styles.grid}>
      <SummaryCard
        label="Refunded to customers"
        icon={CircleCheck}
        tone="success"
        value={formatMinorCurrency(summary.succeededAmountMinor, summary.currency)}
        hint={`${formatNumber(summary.succeededCount)} confirmed ${pluralize(summary.succeededCount, 'refund')}`}
      />
      <SummaryCard
        label="Awaiting settlement"
        icon={Clock}
        tone={summary.pendingAmountMinor > 0 ? 'warning' : 'neutral'}
        value={formatMinorCurrency(summary.pendingAmountMinor, summary.currency)}
        hint={
          summary.needsAttentionCount > 0
            ? `${formatNumber(summary.needsAttentionCount)} need attention`
            : `${formatNumber(summary.pendingCount)} in flight`
        }
        emphasis={summary.needsAttentionCount > 0}
      />
      <SummaryCard
        label="Failed attempts"
        icon={CircleSlash}
        tone={summary.failedCount > 0 ? 'danger' : 'neutral'}
        value={formatNumber(summary.failedCount)}
        hint={
          summary.failedCount > 0
            ? `${formatMinorCurrency(summary.failedAmountMinor, summary.currency)} not refunded`
            : 'Nothing rejected by Paymob'
        }
      />
      <SummaryCard
        label="Refunds in period"
        icon={undefined}
        tone="neutral"
        value={formatNumber(summary.totalCount)}
        hint="Every attempt matching the current filters"
      />
    </div>
  )
}

interface SummaryCardProps {
  label: string
  value: string
  hint: string
  icon: LucideIcon | undefined
  tone: 'success' | 'warning' | 'danger' | 'neutral'
  emphasis?: boolean
}

function SummaryCard({ label, value, hint, icon: Icon, tone, emphasis = false }: SummaryCardProps) {
  return (
    <div className={cn(styles.card, styles[tone], emphasis && styles.emphasis)}>
      <div className={styles.top}>
        <span className={styles.label}>{label}</span>
        {Icon ? (
          <span className={styles.icon}>
            <Icon aria-hidden="true" />
          </span>
        ) : null}
      </div>
      <p className={styles.value} title={value}>
        {value}
      </p>
      <p className={styles.hint}>{hint}</p>
    </div>
  )
}
